import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatTemplateToken,
  jsonPointerGet,
  splitTemplate,
} from '@cotera/utilities';
import type { Editor, Range } from '@tiptap/react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { Switch } from '@cotera/client/v0/components/ui/switch';
import { listJsonPointerPaths } from './node/edit-strategies/json-pointer-suggestions';
import {
  decodeToken,
  type UpstreamSourceGroup,
} from './node/edit-strategies/variable-picker-data';
import { TokenInput } from './node/edit-strategies/tiptap/token-input';
import { editorToTemplate } from './node/edit-strategies/tiptap/token-serialization';
import type { UpstreamFieldOption } from './node/edit-strategies/variable-picker-data';
import { useWorkflowRunInputSample } from '../hooks/use-workflow-run-input-sample';

type WorkflowIdempotencyKey = PersistedWorkflow.WorkflowIdempotencyKey;

type WorkflowIdempotencyKeyEditorProps = {
  value: WorkflowIdempotencyKey | null;
  onChange: (value: WorkflowIdempotencyKey | null) => void;
  disabled?: boolean;
};

type PointerOption = {
  readonly pointer: string;
  readonly sourceName: string;
  readonly leaf: string;
  readonly depth: number;
  readonly value: unknown;
  readonly searchText: string;
};

function buildPointerOptions(
  sample: unknown,
  pointers: readonly string[],
  sourceName: string
): PointerOption[] {
  return pointers.map((pointer) => {
    const segments = pointer
      .split('/')
      .filter((segment) => segment !== '')
      .map(decodeToken);
    const leaf = segments[segments.length - 1] ?? pointer;
    return {
      pointer,
      sourceName,
      leaf,
      depth: Math.max(0, segments.length - 1),
      value: jsonPointerGet(sample, pointer),
      searchText: `${sourceName} ${segments.join(' ')}`,
    };
  });
}

/** `/foo/bar` -> `foo.bar` for matching one selected token back to a pointer. */
function pointerFromTemplate(template: string): string {
  const segments = splitTemplate(template.trim());
  if (segments.length !== 1) {
    return '';
  }
  const [segment] = segments;
  if (segment?.t !== 'ref' || !segment.ref.startsWith('/')) {
    return '';
  }
  return segment.ref;
}

function templateFromValue(value: WorkflowIdempotencyKey | null): string {
  if (value === null || value === undefined) {
    return '';
  }
  if ('pointer' in value) {
    return formatTemplateToken(value.pointer!);
  }
  return value.template;
}

function hasContentBeforeRange(editor: Editor, range: Range): boolean {
  if (range.from <= 1) {
    return false;
  }
  const before = editor.state.doc.textBetween(0, range.from, '', (node) =>
    node.type.name === 'variablePill'
      ? formatTemplateToken(String(node.attrs['ref'] ?? ''))
      : ''
  );
  return before.trim() !== '';
}

function insertKeyField(
  editor: Editor,
  field: UpstreamFieldOption,
  range?: Range
): void {
  const chain = editor.chain().focus();
  if (range !== undefined && range.from < range.to) {
    if (hasContentBeforeRange(editor, range)) {
      chain.deleteRange(range).insertContent(':');
    } else {
      chain.deleteRange(range);
    }
  } else if (editorToTemplate(editor).trim() !== '') {
    chain.insertContent(':');
  }
  chain
    .insertVariablePill({
      ref: field.ref,
    })
    .run();
}

export const WorkflowIdempotencyKeyEditor: React.FC<
  WorkflowIdempotencyKeyEditorProps
> = ({ value, onChange, disabled = false }) => {
  const { sample, triggerKind } = useWorkflowRunInputSample();
  const [enabled, setEnabled] = useState(value !== null);
  const [template, setTemplate] = useState(() => templateFromValue(value));

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (value !== null) {
      setEnabled(true);
      setTemplate(templateFromValue(value));
    }
  }, [value]);

  const pointerPaths = useMemo(() => {
    if (sample === null) {
      return [];
    }
    return listJsonPointerPaths(sample).sort();
  }, [sample]);

  const sourceName = triggerKind ?? 'run input';

  const pointerOptions = useMemo(
    () =>
      sample !== null
        ? buildPointerOptions(sample, pointerPaths, sourceName)
        : [],
    [sample, pointerPaths, sourceName]
  );

  const sources = useMemo<UpstreamSourceGroup[]>(
    () => [
      {
        sourceName,
        label:
          triggerKind === 'cron'
            ? 'Cron input'
            : triggerKind === 'webhook'
            ? 'Webhook input'
            : 'Run input',
        sourceDepth: 1,
        fields: pointerOptions.map((option) => ({
          ref: option.pointer,
          sourceName,
          label: sourceName,
          leaf: option.leaf,
          depth: option.depth,
          sourceDepth: 1,
          value: option.value,
          searchText: option.searchText,
        })),
      },
    ],
    [pointerOptions, sourceName, triggerKind]
  );

  const pointer = useMemo(() => pointerFromTemplate(template), [template]);

  const nextConfig = useMemo((): WorkflowIdempotencyKey | null => {
    if (!enabled) {
      return null;
    }
    if (pointer !== '') {
      return { strategy: 'value', pointer };
    }
    if (template.trim() === '') {
      return null;
    }
    return { strategy: 'value', template };
  }, [enabled, pointer, template]);

  useEffect(() => {
    if (!enabled) {
      onChangeRef.current(null);
      return;
    }
    onChangeRef.current(nextConfig);
  }, [enabled, nextConfig]);

  const validationError = useMemo(() => {
    if (!enabled) {
      return null;
    }
    if (template.trim() === '') {
      return 'Select an input field';
    }
    if (pointer === '' && splitTemplate(template).length === 1) {
      return 'Select an input field with /';
    }
    return null;
  }, [enabled, pointer, template]);

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <Text.P className="text-sm font-medium">Run deduplication</Text.P>
          <Text.Caption className="text-muted-foreground block">
            Skip starting a new run when this workflow already ran with the same
            key from{' '}
            {triggerKind === 'cron'
              ? 'cron input'
              : triggerKind === 'webhook'
              ? 'webhook input'
              : 'run input'}
            .
          </Text.Caption>
        </div>
        <Switch
          checked={enabled}
          disabled={disabled}
          onCheckedChange={(checked) => {
            setEnabled(checked);
            if (!checked) {
              setTemplate('');
              onChangeRef.current(null);
            }
          }}
        />
      </div>
      {enabled ? (
        <div className="space-y-1.5">
          <Text.Caption className="text-muted-foreground block">
            Input field
          </Text.Caption>
          <TokenInput
            value={template}
            placeholder={
              pointerOptions.length > 0
                ? 'Type / to select input fields'
                : 'No input fields available'
            }
            disabled={disabled || pointerOptions.length === 0}
            sources={sources}
            onChange={setTemplate}
            insertVariable={insertKeyField}
          />
          {validationError !== null ? (
            <Text.Caption className="text-destructive block">
              {validationError}
            </Text.Caption>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
